import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Lock, Loader2, AlertCircle, Check } from "lucide-react";
import { AnimatedModal } from "./common/AnimatedModal";
import { usePageAccess } from "../contexts/CurrentUserContext";
import type { PlanFeatureKey } from "../hooks/useCurrentUser";
import api from "../services/api";

interface PlanCard {
  id: number;
  name: string;
  price: number;
  features: Array<{ name: string; included: boolean }>;
}

const FEATURE_LABEL: Partial<Record<PlanFeatureKey, string>> = {
  create_campaign: "Create a campaign",
  run_campaign: "Run a campaign",
  send_outreach: "Send outreach",
  deploy_ads: "Deploy ads",
  view_full_prospect_contact: "Full prospect contact info",
  view_full_ad_copy: "Full ad copy",
  view_performance_metrics: "Performance metrics",
};

interface PaywallContextValue {
  open: (feature: PlanFeatureKey, opts?: { reason?: string }) => void;
  close: () => void;
}

const PaywallContext = createContext<PaywallContextValue>({
  open: () => {},
  close: () => {},
});

export const usePaywall = (): PaywallContextValue => useContext(PaywallContext);

export const PaywallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenantId, plan: currentPlan, refetchUser } = usePageAccess();

  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState<PlanFeatureKey | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  const [plans, setPlans] = useState<PlanCard[] | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setSubmitError(null);
    setSubmittingId(null);
  }, []);

  const openPaywall = useCallback(
    (f: PlanFeatureKey, opts?: { reason?: string }) => {
      setFeature(f);
      setReason(opts?.reason || null);
      setOpen(true);
    },
    [],
  );

  // Lazily load plans when the modal opens for the first time.
  useEffect(() => {
    if (!open || plans) return;
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    (async () => {
      try {
        const r = await api.get<{ success: boolean; plans: PlanCard[] }>(
          "/onboarding/plans",
        );
        if (!cancelled) {
          // Hide the Free plan from the upgrade picker — they're already on it.
          setPlans((r.plans || []).filter((p) => p.name.toLowerCase() !== "free"));
        }
      } catch (err) {
        if (!cancelled) setPlansError(err instanceof Error ? err.message : "Failed to load plans");
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, plans]);

  const upgrade = async (planId: number) => {
    if (!tenantId) {
      setSubmitError("No active tenant");
      return;
    }
    setSubmittingId(planId);
    setSubmitError(null);
    try {
      const r = await api.post<{ success: boolean; payment_link?: string; error?: string }>(
        `/tenants/${tenantId}/plan/change`,
        { planId },
      );
      if (!r.success || !r.payment_link) {
        setSubmitError(r.error || "Could not start upgrade");
        setSubmittingId(null);
        return;
      }
      // Refetch user state — change-plan endpoint sets pending_plan_id; the
      // user's role/plan flips when payment lands. We send them straight to
      // the pay page now.
      refetchUser();
      window.location.href = r.payment_link;
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not start upgrade");
      setSubmittingId(null);
    }
  };

  const featureLabel = feature ? FEATURE_LABEL[feature] || feature : null;
  const contextValue = useMemo(
    () => ({ open: openPaywall, close }),
    [openPaywall, close],
  );

  return (
    <PaywallContext.Provider value={contextValue}>
      {children}
      <AnimatedModal
        isOpen={open}
        onClose={close}
        className="w-full max-w-2xl mx-auto rounded-xl border border-border bg-card shadow-2xl"
      >
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground">
                Upgrade to unlock {featureLabel || "this feature"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {reason ||
                  "Your current plan doesn't include this. Pick a plan below to continue."}
              </p>
              {currentPlan && (
                <p className="mt-1 text-xs text-muted-foreground">
                  You're currently on the <span className="font-medium">{currentPlan.name}</span> plan.
                </p>
              )}
            </div>
          </div>

          {plansLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading plans…
            </div>
          ) : plansError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{plansError}</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(plans || []).map((p) => {
                const isCurrent = currentPlan?.id === p.id;
                const isSubmitting = submittingId === p.id;
                return (
                  <div
                    key={p.id}
                    className={`rounded-xl border p-4 flex flex-col gap-3 ${
                      isCurrent ? "border-primary bg-primary/5" : "border-border bg-background"
                    }`}
                  >
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{p.name}</h3>
                      <p className="text-2xl font-bold text-foreground">
                        ${p.price}
                        <span className="text-xs font-normal text-muted-foreground">/mo</span>
                      </p>
                    </div>
                    <ul className="text-xs space-y-1 flex-1">
                      {(p.features || []).slice(0, 5).map((f, idx) => (
                        <li
                          key={idx}
                          className={`flex items-start gap-1.5 ${
                            f.included ? "text-foreground" : "text-muted-foreground line-through"
                          }`}
                        >
                          <Check
                            className={`h-3 w-3 mt-0.5 shrink-0 ${
                              f.included ? "text-emerald-500" : "text-muted-foreground/40"
                            }`}
                          />
                          {f.name}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => upgrade(p.id)}
                      disabled={isCurrent || isSubmitting || submittingId !== null}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isCurrent
                          ? "bg-muted text-muted-foreground cursor-not-allowed"
                          : "bg-primary text-primary-foreground hover:bg-primary/90"
                      } disabled:opacity-50`}
                    >
                      {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isCurrent ? "Current plan" : isSubmitting ? "Starting…" : `Choose ${p.name}`}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={close}
              className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </AnimatedModal>
    </PaywallContext.Provider>
  );
};
